import * as THREE from 'three';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { mmToMeshUnits } from '../brain/loadCortexMesh';
import { BODY_EXTENT_MM } from '../nervous/anatomy';

export type ViewMode = 'brain' | 'nervous-system';

interface Framing {
  position: THREE.Vector3;
  target: THREE.Vector3;
  minDistance: number;
  maxDistance: number;
}

/**
 * Camera framings for each view.
 *
 * The whole nervous system is roughly ten times the brain's extent, so the two
 * views need very different distances. Rather than snapping between them, the
 * camera eases across, which keeps the spatial relationship legible — you can
 * see the brain shrink into place at the top of the cord instead of being
 * teleported somewhere unrecognisable.
 */
const FRAMINGS: Record<ViewMode, Framing> = {
  brain: {
    position: new THREE.Vector3(4.5, 0.85, 1.9),
    target: new THREE.Vector3(0, -0.1, 0),
    minDistance: 2.5,
    maxDistance: 12,
  },
  'nervous-system': {
    // Distance is derived rather than guessed: the body spans ~17.6 scene units
    // vertically, and at this camera's 45° field of view fitting that (plus
    // margin for the bottom controls overlaying the scene) needs ~26 units of
    // standoff. The target sits above the body's midpoint for the same reason.
    position: new THREE.Vector3(14.3, -2.6, 21.3),
    target: new THREE.Vector3(0, mmToMeshUnits(BODY_EXTENT_MM.top + BODY_EXTENT_MM.bottom) * 0.42, 0),
    minDistance: 4,
    maxDistance: 52,
  },
};

/**
 * Eases the camera between the brain and whole-nervous-system framings.
 *
 * Distance limits are widened for the duration of a transition and only
 * tightened once it settles — clamping mid-flight would fight the animation and
 * make the camera stutter as it crosses the brain view's much smaller range.
 */
/**
 * How far back to pull the camera for the current viewport shape.
 *
 * Three.js expresses field of view vertically, so on a portrait screen the
 * horizontal view is much narrower than the vertical one — and both the brain
 * and the nerve pathways are wider than they are tall. Without compensating,
 * a phone crops them off at the sides.
 */
function responsiveDistanceFactor(aspect: number): number {
  const REFERENCE_ASPECT = 1.3;
  if (aspect >= REFERENCE_ASPECT) return 1;
  return Math.min(2.2, REFERENCE_ASPECT / Math.max(aspect, 0.3));
}

export class ViewModeController {
  private mode: ViewMode = 'brain';
  private progress = 1;
  private readonly fromPosition = new THREE.Vector3();
  private readonly fromTarget = new THREE.Vector3();
  private readonly scratch = new THREE.Vector3();

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly controls: OrbitControls,
  ) {
    this.applyFraming(this.framingFor('brain'));
    window.addEventListener('resize', () => this.handleResize());
  }

  /** The framing for a mode, scaled out to suit the current viewport shape. */
  private framingFor(mode: ViewMode): Framing {
    const base = FRAMINGS[mode];
    const factor = responsiveDistanceFactor(this.camera.aspect);

    return {
      position: this.scratch
        .copy(base.position)
        .sub(base.target)
        .multiplyScalar(factor)
        .add(base.target)
        .clone(),
      target: base.target.clone(),
      minDistance: base.minDistance,
      maxDistance: base.maxDistance * factor,
    };
  }

  private applyFraming(framing: Framing): void {
    this.camera.position.copy(framing.position);
    this.controls.target.copy(framing.target);
    this.controls.minDistance = framing.minDistance;
    this.controls.maxDistance = framing.maxDistance;
    this.controls.update();
  }

  /**
   * Re-frames on viewport change — but only the distance limits and zoom-out
   * headroom, leaving the user's own orbit angle alone. Snapping the camera back
   * to its default on every resize would be hostile on a phone, where the
   * address bar collapsing fires a resize mid-interaction.
   */
  private handleResize(): void {
    if (this.progress < 1) return;

    const framing = this.framingFor(this.mode);
    this.controls.minDistance = framing.minDistance;
    this.controls.maxDistance = framing.maxDistance;

    const desired = framing.position.distanceTo(framing.target);
    const current = this.camera.position.distanceTo(this.controls.target);
    if (current < desired) {
      // Too close to fit the subject at this aspect — ease outward along the
      // direction the user is already looking from.
      this.camera.position
        .sub(this.controls.target)
        .setLength(desired)
        .add(this.controls.target);
    }
    this.controls.update();
  }

  get current(): ViewMode {
    return this.mode;
  }

  get isTransitioning(): boolean {
    return this.progress < 1;
  }

  setMode(mode: ViewMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.progress = 0;
    this.fromPosition.copy(this.camera.position);
    this.fromTarget.copy(this.controls.target);

    // Relax the limits so neither endpoint's clamp interferes while easing.
    const brain = this.framingFor('brain');
    const body = this.framingFor('nervous-system');
    this.controls.minDistance = Math.min(brain.minDistance, body.minDistance);
    this.controls.maxDistance = Math.max(brain.maxDistance, body.maxDistance);
  }

  toggle(): ViewMode {
    this.setMode(this.mode === 'brain' ? 'nervous-system' : 'brain');
    return this.mode;
  }

  /** Advances the transition. Returns true while the camera is still moving. */
  update(deltaSeconds: number): boolean {
    if (this.progress >= 1) return false;

    this.progress = Math.min(1, this.progress + deltaSeconds * 0.9);
    const eased = easeInOutCubic(this.progress);
    const framing = this.framingFor(this.mode);

    this.camera.position.lerpVectors(this.fromPosition, framing.position, eased);
    this.controls.target.lerpVectors(this.fromTarget, framing.target, eased);

    if (this.progress >= 1) {
      this.controls.minDistance = framing.minDistance;
      this.controls.maxDistance = framing.maxDistance;
    }

    return true;
  }
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}
